import { Capacitor, registerPlugin } from '@capacitor/core';

export type NativeTrackingAuthorizationStatus =
  | 'authorized'
  | 'denied'
  | 'restricted'
  | 'notDetermined'
  | 'unsupported';

interface TrackingTransparencyStatusResult {
  status: NativeTrackingAuthorizationStatus;
  canPrompt?: boolean;
}

interface TrackingTransparencyIdResult {
  status: NativeTrackingAuthorizationStatus;
  idfa: string | null;
  idfv?: string | null;
  available: boolean;
}

interface TrackingTransparencyPlugin {
  getStatus(): Promise<TrackingTransparencyStatusResult>;
  requestPermission(): Promise<TrackingTransparencyStatusResult>;
  openSettings(): Promise<{ opened: boolean }>;
  getAdvertisingIdentifier(): Promise<TrackingTransparencyIdResult>;
}

const TrackingTransparency = registerPlugin<TrackingTransparencyPlugin>('TrackingTransparency');

export function isNativeTrackingTransparencyRuntime(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

export async function getNativeTrackingAuthorizationStatus(): Promise<NativeTrackingAuthorizationStatus> {
  if (!isNativeTrackingTransparencyRuntime()) {
    return 'unsupported';
  }

  const { status } = await TrackingTransparency.getStatus();
  return status;
}

export async function requestNativeTrackingPermission(): Promise<NativeTrackingAuthorizationStatus> {
  if (!isNativeTrackingTransparencyRuntime()) {
    return 'unsupported';
  }

  const { status } = await TrackingTransparency.requestPermission();
  return status;
}

export async function openNativeTrackingSettings(): Promise<boolean> {
  if (!isNativeTrackingTransparencyRuntime()) {
    return false;
  }

  const { opened } = await TrackingTransparency.openSettings();
  return opened;
}

export async function getNativeAdvertisingIdentifier(): Promise<TrackingTransparencyIdResult> {
  if (!isNativeTrackingTransparencyRuntime()) {
    return {
      status: 'unsupported',
      idfa: null,
      idfv: null,
      available: false,
    };
  }

  return TrackingTransparency.getAdvertisingIdentifier();
}
